import { GetBusinessIO } from '../types';
import { useJustfiIntegration1Queries } from './use-justfi-integration1-queries';
import { UseJustfiIntegration1QueryEndpointArgs, useJustfiIntegration1Query } from './use-justfi-integration1-query';

/**
 * A hook that returns a query for the `GetBusiness` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetBusinessQuery = <E = unknown, D = GetBusinessIO['output']>(
  args: UseJustfiIntegration1QueryEndpointArgs<'GetBusiness', E, D>
) =>
  useJustfiIntegration1Query({
    endpointName: 'GetBusiness',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetBusiness` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetBusinessQueries = <E = unknown, D = GetBusinessIO['output']>(
  argsArr: UseJustfiIntegration1QueryEndpointArgs<'GetBusiness', E, D>[]
) =>
  useJustfiIntegration1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetBusiness',
      ...args,
    }))
  );
